import { setParam } from "./requestCommon";
import { trackEvent } from "./omega";

const TOKEN_REQUEST_TIMEOUT_MS = 1500;
const TOKEN_RETRY_DELAY_MS = 50;

let tokenReadyPromise: Promise<string> | null = null;

/**
 * 初始化 App 的 Sofipo token。所有 loader 共用同一个 Promise，
 * 确保首屏接口在 token 写入公共参数后再发起。
 */
export function initializeSofipoToken(): Promise<string> {
  // App 和所有 loader 共用同一个初始化任务，避免重复调用 Bridge。
  if (tokenReadyPromise) {
    return tokenReadyPromise;
  }

  tokenReadyPromise = new Promise<string>((resolve) => {
    // 首次失败后最多再请求一次。
    let retryCount = 0;

    const requestToken = () => {
      // 防止 Bridge 回调和超时回调重复结束同一次请求。
      let completed = false;
      const finish = (token: string) => {
        if (completed) return;
        completed = true;
        window.clearTimeout(timer);

        if (token) {
          // 先写入公共参数再唤醒 loader，确保后续接口能够携带 token。
          setParam("token", token);
          resolve(token);
        } else if (retryCount < 1) {
          // Bridge 未就绪、抛错、返回空值或超时时，延迟后重试一次。
          // 埋点：记录首次 token 获取失败，便于统计重试触发次数。
          trackEvent("tech_sofi_catch_error", {
            source: "utils.authToken.sfGetSofipoTicket",
            data: "Initial token request failed",
          });
          retryCount++;
          window.setTimeout(requestToken, TOKEN_RETRY_DELAY_MS);
        } else {
          // 两次都失败时结束等待，避免 loader 永久阻塞。
          // 埋点：记录重试后仍获取失败，用于识别最终无 token 的请求。
          trackEvent("tech_sofi_catch_error", {
            source: "utils.authToken.sfGetSofipoTicket",
            data: "Token retry failed",
          });
          resolve("");
        }
      };
      // Bridge 没有回调时主动结束本次尝试，以便进入重试或降级流程。
      const timer = window.setTimeout(() => finish(""), TOKEN_REQUEST_TIMEOUT_MS);

      try {
        const bridge = window.Fusion?.loadModule("SofiBridgeModule");
        if (!bridge || typeof bridge.sfGetSofipoTicket !== "function") {
          finish("");
          return;
        }
        bridge.sfGetSofipoTicket(finish);
      } catch {
        finish("");
      }
    };

    requestToken();
  });

  return tokenReadyPromise;
}

export function waitForSofipoToken(): Promise<string> {
  return tokenReadyPromise || initializeSofipoToken();
}
